/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import { primitiveColorTokensV2CssVars } from '@uber/base-specs/src/generated/base-color-tokens-v2';
import { V1_PRIMITIVE_TO_V2_PRIMITIVE, V1_SEMANTIC_TO_V2_SEMANTIC, mapV1ColorTokenToV2 } from '@uber/base-specs/src/utils/mapV1ToV2Colors';
const V2_PRIMITIVE_KEYS: ReadonlySet<string> = new Set(Object.keys(primitiveColorTokensV2CssVars));

/**
 * The v1 → v2 mappings that rename a key, resolved once at module load.
 *
 * {@link applyV1ColorUpgrades} acts only on cross-key mappings, so it walks
 * these 452 entries rather than all ~1050 theme keys, most of which are
 * component colors with no mapping at all. Resolve through
 * `mapV1ColorTokenToV2` rather than reading the two tables directly, so
 * primitive-before-semantic precedence stays defined in one place.
 */
const CROSS_KEY_MAPPINGS: ReadonlyArray<{
  v1Key: string;
  v2Key: string;
}> = (() => {
  const v1Keys = new Set([...Object.keys(V1_PRIMITIVE_TO_V2_PRIMITIVE), ...Object.keys(V1_SEMANTIC_TO_V2_SEMANTIC)]);
  const mappings: Array<{
    v1Key: string;
    v2Key: string;
  }> = [];
  for (const v1Key of v1Keys) {
    const v2Key = mapV1ColorTokenToV2(v1Key);
    if (v2Key && v2Key !== v1Key) mappings.push({
      v1Key,
      v2Key
    });
  }
  return mappings;
})();

/**
 * The boundary the v1 → v2 merge splits on: colliding primitives are renamed
 * (their values diverge so a silent replacement would be a footgun), while
 * colliding semantics are overwritten (that IS the migration). Exported for
 * tests; internal.
 */
export function isV2PrimitiveKey(key: string): boolean {
  return V2_PRIMITIVE_KEYS.has(key);
}

/**
 * The v1 → v2 color merge can't be a flat spread because the same token name
 * means opposite things for different categories:
 *
 *   - For PRIMITIVES, `theme.colors.blue100` is `#DEE9FE` (a light tint) in
 *     v1 and a CSS var resolving to pure white in v2.
 *
 *   - For SEMANTICS, `theme.colors.backgroundPrimary` IS the migration
 *     target — opting into `colors: true` is signalling "I want the v2 CSS
 *     var that updates per `data-theme`/`data-product`." v2 wins on the
 *     canonical name.
 *
 * v2-only keys (no collision) are passed through unchanged either way.
 * Exported for tests; internal.
 */
export function renameCollidingV2Keys(v1Colors: Record<string, unknown>, v2Colors: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  applyRenamedV2Keys(out, v1Colors, v2Colors);
  return out;
}

/**
 * In-place form of {@link renameCollidingV2Keys}: writes into `out` rather
 * than returning a table for the caller to merge.
 *
 * Collision detection reads `v1Colors`, the original theme colors — not `out`,
 * which may already hold v2 values written by an earlier pass.
 */
export function applyRenamedV2Keys(out: Record<string, unknown>, v1Colors: Record<string, unknown>, v2Colors: Record<string, string>): void {
  for (const key of Object.keys(v2Colors)) {
    const collides = Object.prototype.hasOwnProperty.call(v1Colors, key);
    const target = collides && isV2PrimitiveKey(key) ? `${key}v2` : key;
    out[target] = v2Colors[key];
  }
}

/**
 * For each v1 color key whose mapping resolves to a **different** v2 key
 * (cross-key renames, e.g. `amber100 → amber96`), replace the stored value
 * with the corresponding v2 CSS-var value — unless the caller has overridden
 * that token (detected by value inequality against the `v1Colors` baseline).
 *
 * Same-key semantic overwrites (e.g. `backgroundPrimary → backgroundPrimary`)
 * are already handled by `renameCollidingV2Keys` and are intentionally left
 * out of scope here to preserve the existing unconditional-overwrite contract
 * for those tokens.
 *
 * Exported for tests; internal.
 */
export function upgradeV1ColorsToV2(themeColors: Record<string, unknown>, v1Colors: Record<string, unknown>, v2Colors: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = Object.assign({}, themeColors);
  applyV1ColorUpgrades(out, themeColors, v1Colors, v2Colors);
  return out;
}

/**
 * In-place form of {@link upgradeV1ColorsToV2}: writes into `out` rather than
 * returning a copy.
 *
 * `themeColors` stays a separate parameter because the override check must
 * compare against pre-upgrade values, which `out` no longer holds once an
 * earlier pass has written to it.
 */
export function applyV1ColorUpgrades(out: Record<string, unknown>, themeColors: Record<string, unknown>, v1Colors: Record<string, unknown>, v2Colors: Record<string, string>): void {
  for (let i = 0; i < CROSS_KEY_MAPPINGS.length; i++) {
    const {
      v1Key,
      v2Key
    } = CROSS_KEY_MAPPINGS[i];
    // This loop walks the mapping table, not the baseline, so a key the
    // baseline never had would otherwise slip through: both sides of the
    // override check below would read `undefined` and compare equal.
    if (!Object.prototype.hasOwnProperty.call(v1Colors, v1Key)) continue;
    const v2Value = v2Colors[v2Key];
    if (v2Value === undefined) continue;
    if (themeColors[v1Key] !== v1Colors[v1Key]) continue;
    out[v1Key] = v2Value;
  }
}